import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Crown, Lock, Globe, Navigation, ChevronDown, ChevronUp, Palmtree, Home, Landmark, Castle, PawPrint, Drama, Camera, Lightbulb, Map } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import SEO from '../../components/SEO';

const ExploreTour = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRegions, setExpandedRegions] = useState({});

  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  // Region display info
  const regionInfo = {
    'usa_west': { label: 'USA West Coast', Icon: Palmtree, color: '#E10600' },
    'usa_midwest': { label: 'USA Midwest', Icon: Home, color: '#06B6D4' },
    'usa_east': { label: 'USA East Coast', Icon: Landmark, color: '#DC143C' },
    'europe': { label: 'Europe', Icon: Castle, color: '#9B59B6' },
    'uk': { label: 'United Kingdom', Icon: Crown, color: '#E74C3C' },
    'asia': { label: 'Asia', Icon: Castle, color: '#FF6B6B' },
    'australia': { label: 'Australia', Icon: PawPrint, color: '#00CED1' },
    'south_america': { label: 'South America', Icon: Drama, color: '#2ECC71' },
    'middle_east': { label: 'Middle East', Icon: Landmark, color: '#F39C12' },
  };

  // All 44 locations data (matching the app)
  const allLocations = [
    // USA West (13)
    { name: 'Hollywood Walk of Fame Star', city: 'Los Angeles', country: 'United States', region: 'usa_west' },
    { name: 'Forest Lawn Memorial Park', city: 'Glendale', country: 'United States', region: 'usa_west' },
    { name: 'Neverland Ranch', city: 'Los Olivos', country: 'United States', region: 'usa_west' },
    { name: 'Grammy Museum', city: 'Los Angeles', country: 'United States', region: 'usa_west' },
    { name: 'MJ ONE - Cirque du Soleil', city: 'Las Vegas', country: 'United States', region: 'usa_west' },
    { name: 'Hayvenhurst Estate', city: 'Encino', country: 'United States', region: 'usa_west' },
    { name: 'Staples Center', city: 'Los Angeles', country: 'United States', region: 'usa_west' },
    { name: 'Record Plant Studios', city: 'Los Angeles', country: 'United States', region: 'usa_west' },
    { name: 'Westlake Recording Studios', city: 'Los Angeles', country: 'United States', region: 'usa_west' },
    { name: 'Carolwood Estate', city: 'Los Angeles', country: 'United States', region: 'usa_west' },
    { name: 'UCLA Medical Center', city: 'Los Angeles', country: 'United States', region: 'usa_west' },
    { name: 'Gardner Street Elementary', city: 'Hollywood', country: 'United States', region: 'usa_west' },
    { name: 'Sony Music Studios', city: 'Los Angeles', country: 'United States', region: 'usa_west' },
    // USA Midwest (3)
    { name: 'Michael Jackson Birthplace', city: 'Gary', country: 'United States', region: 'usa_midwest' },
    { name: 'Motown Museum (Hitsville U.S.A.)', city: 'Detroit', country: 'United States', region: 'usa_midwest' },
    { name: 'Rock & Roll Hall of Fame', city: 'Cleveland', country: 'United States', region: 'usa_midwest' },
    // USA East (2)
    { name: 'Apollo Theater', city: 'New York City', country: 'United States', region: 'usa_east' },
    { name: 'National Museum of African American History', city: 'Washington D.C.', country: 'United States', region: 'usa_east' },
    // Europe (8)
    { name: 'Michael Jackson Memorial', city: 'Munich', country: 'Germany', region: 'europe' },
    { name: 'King of Pop Museum', city: 'Steffisburg', country: 'Switzerland', region: 'europe' },
    { name: 'Michael Jackson Exhibition', city: 'Budapest', country: 'Hungary', region: 'europe' },
    { name: 'Amsterdam MJ Sites', city: 'Amsterdam', country: 'Netherlands', region: 'europe' },
    { name: 'Paris MJ Tribute', city: 'Paris', country: 'France', region: 'europe' },
    { name: 'Monte Carlo Casino', city: 'Monaco', country: 'Monaco', region: 'europe' },
    { name: 'Rome Fan Sites', city: 'Rome', country: 'Italy', region: 'europe' },
    { name: 'Berlin Memorial', city: 'Berlin', country: 'Germany', region: 'europe' },
    // UK (3)
    { name: 'The O2 Arena', city: 'London', country: 'United Kingdom', region: 'uk' },
    { name: 'Wembley Stadium', city: 'London', country: 'United Kingdom', region: 'uk' },
    { name: 'Madame Tussauds', city: 'London', country: 'United Kingdom', region: 'uk' },
    // Asia (9)
    { name: 'Tokyo Dome', city: 'Tokyo', country: 'Japan', region: 'asia' },
    { name: 'Tokyo MJ Fan Sites', city: 'Tokyo', country: 'Japan', region: 'asia' },
    { name: 'Hong Kong MJ Sites', city: 'Hong Kong', country: 'China', region: 'asia' },
    { name: 'Singapore Tribute', city: 'Singapore', country: 'Singapore', region: 'asia' },
    { name: 'Taipei Arena', city: 'Taipei', country: 'Taiwan', region: 'asia' },
    { name: 'Seoul MJ Memorial', city: 'Seoul', country: 'South Korea', region: 'asia' },
    { name: 'Osaka Sites', city: 'Osaka', country: 'Japan', region: 'asia' },
    { name: 'Bangkok Tribute', city: 'Bangkok', country: 'Thailand', region: 'asia' },
    { name: 'Manila Concert Site', city: 'Manila', country: 'Philippines', region: 'asia' },
    // Australia (2)
    { name: 'Melbourne MJ Street Art', city: 'Melbourne', country: 'Australia', region: 'australia' },
    { name: 'Sydney Madame Tussauds', city: 'Sydney', country: 'Australia', region: 'australia' },
    // South America (3)
    { name: 'Santa Marta Favela MJ Statue', city: 'Rio de Janeiro', country: 'Brazil', region: 'south_america' },
    { name: 'Pelourinho MJ Filming Location', city: 'Salvador', country: 'Brazil', region: 'south_america' },
    { name: 'Buenos Aires Tribute', city: 'Buenos Aires', country: 'Argentina', region: 'south_america' },
    // Middle East (1)
    { name: 'Dubai Madame Tussauds', city: 'Dubai', country: 'UAE', region: 'middle_east' },
  ];

  // Featured free locations (3 iconic ones)
  const freeLocations = [
    { 
      name: 'Michael Jackson Birthplace', 
      city: 'Gary, Indiana',
      description: 'The modest two-bedroom house where the King of Pop was born and spent his early childhood.',
      region: 'usa_midwest'
    },
    { 
      name: 'Hollywood Walk of Fame Star', 
      city: 'Los Angeles, California',
      description: 'Michael\'s star on the famous Hollywood Boulevard, a pilgrimage site for fans worldwide.',
      region: 'usa_west'
    },
    { 
      name: 'Munich Memorial', 
      city: 'Munich, Germany',
      description: 'The permanent memorial outside the Bayerischer Hof hotel, decorated daily by fans.',
      region: 'europe'
    },
  ];

  const fetchLocations = useCallback(async () => {
    try {
      const response = await apiFetch('/api/mj-tourism/locations');
      if (response.ok) {
        const data = await response.json();
        const locs = data.locations || data.all_locations || data || [];
        if (locs.length > 0) {
          setLocations(locs);
        } else {
          setLocations(allLocations);
        }
      } else {
        setLocations(allLocations);
      }
    } catch (err) {
      setLocations(allLocations);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Group locations by region
  const locationsByRegion = (locations.length > 0 ? locations : allLocations).reduce((acc, loc) => {
    const region = loc.region || 'other';
    if (!acc[region]) acc[region] = [];
    acc[region].push(loc);
    return acc;
  }, {});

  const toggleRegion = (region) => {
    setExpandedRegions(prev => ({ ...prev, [region]: !prev[region] }));
  };

  const totalLocations = locations.length || 44;

  return (
    <div className="min-h-screen pt-20" data-testid="world-tour-page">
      <SEO title="Michael Jackson World Tour Locations — 44 Iconic Places Worldwide | PopCrown" description="Visit 44 Michael Jackson locations around the world. Neverland Ranch, Motown Museum, Apollo Theater, Staples Center, and filming locations from Thriller, Beat It, and more." keywords="Michael Jackson locations, MJ world tour, Neverland Ranch, Michael Jackson filming locations, Thriller location, Beat It location, Apollo Theater Michael Jackson, Motown Museum, Michael Jackson tourism, MJ places to visit" canonical="/explore/tour" />
      {/* Header */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4">
          <Link to="/explore" className="inline-flex items-center gap-2 text-[#E10600] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" />
            Back to Explore
          </Link>
          <div className="flex items-center gap-4">
            <MapPin className="w-12 h-12 text-[#06B6D4]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                MJ <span className="thriller-text">Journey</span>
              </h1>
              <p className="text-gray-400">{totalLocations} iconic locations across 9 regions worldwide</p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map Preview */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #000 0%, #1a1a1a 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Map Hero */}
            <div className="relative rounded-2xl overflow-hidden mb-8" style={{
              background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
              border: '2px solid rgba(6, 182, 212, 0.3)',
              height: '350px'
            }}>
              <div className="absolute inset-0 opacity-20">
                <img 
                  src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?q=80&w=2000" 
                  alt="Michael Jackson world tour map - MJ locations worldwide - King of Pop pilgrimage sites"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Stats Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <Globe className="w-16 h-16 text-[#06B6D4] mx-auto mb-4" />
                  <h3 className="text-3xl font-bold text-white mb-2">{totalLocations} Locations</h3>
                  <p className="text-gray-400 mb-2">9 Regions • 20+ Countries</p>
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {Object.entries(regionInfo).slice(0, 5).map(([key, info]) => (
                      <span key={key} className="text-xl" title={info.label}>{info.emoji}</span>
                    ))}
                    <span className="text-gray-400">+4 more</span>
                  </div>
                </div>
              </div>

              {/* Animated Pins */}
              <div className="absolute top-1/4 left-1/5 w-3 h-3 bg-[#E10600] rounded-full animate-ping"></div>
              <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-[#DC143C] rounded-full animate-ping" style={{ animationDelay: '0.3s' }}></div>
              <div className="absolute bottom-1/3 left-1/2 w-3 h-3 bg-[#06B6D4] rounded-full animate-ping" style={{ animationDelay: '0.6s' }}></div>
              <div className="absolute top-1/2 right-1/3 w-3 h-3 bg-[#9B59B6] rounded-full animate-ping" style={{ animationDelay: '0.9s' }}></div>
              <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-[#2ECC71] rounded-full animate-ping" style={{ animationDelay: '1.2s' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Preview Locations */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-bold mb-3">
                FREE PREVIEW
              </span>
              <h2 className="text-2xl font-bold text-gray-900">3 Iconic Locations</h2>
              <p className="text-gray-600">Start your journey at these must-visit sites</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {freeLocations.map((loc, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-[#06B6D4]/30 hover:border-[#06B6D4] transition-colors" data-testid={`free-location-${index}`}>
                  <div className="h-3" style={{ background: regionInfo[loc.region]?.color || '#06B6D4' }}></div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      {(() => {
                        const RegionIcon = regionInfo[loc.region]?.Icon;
                        return RegionIcon ? <RegionIcon className="w-5 h-5" strokeWidth={1.5} style={{ color: regionInfo[loc.region]?.color }} aria-hidden="true" /> : null;
                      })()}
                      <span className="text-xs font-semibold text-gray-500 uppercase">{regionInfo[loc.region]?.label}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{loc.name}</h3>
                    <p className="text-[#06B6D4] text-sm font-medium mb-3">{loc.city}</p>
                    <p className="text-gray-600 text-sm">{loc.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* All Regions Overview */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Explore All Regions</h2>
              <p className="text-gray-600">{totalLocations - 3} more locations available for members</p>
            </div>

            <div className="space-y-4 mb-12">
              {Object.entries(regionInfo).map(([regionKey, info]) => {
                const regionLocations = locationsByRegion[regionKey] || [];
                const count = regionLocations.length;
                const isExpanded = expandedRegions[regionKey];
                
                if (count === 0 && !loading) return null;
                
                return (
                  <div key={regionKey} className="bg-white rounded-xl shadow-md overflow-hidden" data-testid={`region-${regionKey}`}>
                    <button 
                      onClick={() => toggleRegion(regionKey)}
                      className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{info.emoji}</span>
                        <div className="text-left">
                          <h3 className="font-bold text-gray-900">{info.label}</h3>
                          <p className="text-sm text-gray-500">{count || '...'} locations</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ background: info.color }}></div>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="border-t border-gray-100 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {regionLocations.map((loc, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                              <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-gray-900 text-sm font-medium truncate">{loc.name}</p>
                                <p className="text-gray-500 text-xs truncate">{loc.city}, {loc.country}</p>
                              </div>
                              {loc.googleMapsUrl && (
                                <Navigation className="w-4 h-4 text-[#06B6D4] flex-shrink-0" />
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 text-center">
                          <a 
                            href={subscriptionJoinLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[#E10600] text-black rounded-full text-sm font-bold hover:bg-[#FFE44D] transition-colors"
                            data-testid="unlock-region-cta"
                          >
                            <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-4 h-4 object-contain" />
                            Unlock Full Details
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* What Members Get */}
      <section className="py-12" style={{ background: '#0a0a0a' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-12 h-12 mx-auto mb-4 object-contain" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              What Members <span className="thriller-text">Unlock</span>
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { Icon: MapPin, label: 'Full Addresses', desc: 'Exact locations & directions' },
                { Icon: Camera, label: 'Photo Galleries', desc: 'Fan photos & memories' },
                { Icon: Lightbulb, label: 'Visitor Tips', desc: 'Best times & local advice' },
                { Icon: Map, label: 'Google Maps', desc: 'One-tap navigation' },
              ].map((item, idx) => {
                const IconEl = item.Icon;
                return (
                  <div key={idx} className="p-4 rounded-xl" style={{ background: '#1a1a1a' }}>
                    <IconEl className="w-7 h-7 mb-2 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
                    <h4 className="text-white font-bold text-sm mb-1">{item.label}</h4>
                    <p className="text-gray-500 text-xs">{item.desc}</p>
                  </div>
                );
              })}
            </div>

            <a href={subscriptionJoinLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block">
              Start Your Journey
            </a>
            <p className="text-gray-500 text-sm mt-4">Join the community • Plan your MJ pilgrimage</p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-black">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 text-xs">
            Location information compiled from public sources. PopCrown is not affiliated with any venues, estates, or rights holders.
            Always verify visiting hours and accessibility before traveling.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ExploreTour;
